import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

interface NutritionLoadingScreenProps {
    minutesLeft: number;
    startTime?: number; // Optional fallbacks
    targetTime?: number;
    coachMessage?: string;
    onDevComplete: () => void;
    isPerformingLoad: boolean;
    isUpdate?: boolean; // V336: Visual feedback distinction
}

const NutritionLoadingScreen: React.FC<NutritionLoadingScreenProps> = ({
    minutesLeft,
    startTime = Date.now(),
    targetTime = Date.now() + 1000 * 60 * 60, // Default 1h
    coachMessage = "Trabajando...",
    onDevComplete,
    isPerformingLoad,
    isUpdate = false
}) => {
    const { marcAvatarUrl, userRole } = useAuth();
    const { t } = useTranslation();
    // Steps for "Flavor" text
    const initialSteps = [
        { title: t('nutrition_loading.steps.initial.0.title'), desc: t('nutrition_loading.steps.initial.0.desc'), icon: "analytics" },
        { title: t('nutrition_loading.steps.initial.1.title'), desc: t('nutrition_loading.steps.initial.1.desc'), icon: "calculate" },
        { title: t('nutrition_loading.steps.initial.2.title'), desc: t('nutrition_loading.steps.initial.2.desc'), icon: "nutrition" },
        { title: t('nutrition_loading.steps.initial.3.title'), desc: t('nutrition_loading.steps.initial.3.desc'), icon: "shopping_basket" },
        { title: t('nutrition_loading.steps.initial.4.title'), desc: t('nutrition_loading.steps.initial.4.desc'), icon: "menu_book" },
        { title: t('nutrition_loading.steps.initial.5.title'), desc: t('nutrition_loading.steps.initial.5.desc'), icon: "verified" },
        { title: t('nutrition_loading.steps.initial.6.title'), desc: t('nutrition_loading.steps.initial.6.desc'), icon: "restaurant" }
    ];

    const updateSteps = [
        { title: t('nutrition_loading.steps.update.0.title'), desc: t('nutrition_loading.steps.update.0.desc'), icon: "mark_chat_read" },
        { title: t('nutrition_loading.steps.update.1.title'), desc: t('nutrition_loading.steps.update.1.desc'), icon: "calculate" },
        { title: t('nutrition_loading.steps.update.2.title'), desc: t('nutrition_loading.steps.update.2.desc'), icon: "restaurant_menu" },
        { title: t('nutrition_loading.steps.update.3.title'), desc: t('nutrition_loading.steps.update.3.desc'), icon: "rule" },
        { title: t('nutrition_loading.steps.update.4.title'), desc: t('nutrition_loading.steps.update.4.desc'), icon: "check_circle" }
    ];

    const steps = isUpdate ? updateSteps : initialSteps;

    // Helper: Format Time Visually
    const formatVisualTime = (ms: number) => {
        if (ms <= 0) return t('nutrition_loading.almost_ready');

        // V332: User requested NO minutes and simplified hours (Ceiling)
        const totalMinutes = Math.ceil(ms / (1000 * 60));

        if (totalMinutes >= 60) {
            const h = Math.ceil(totalMinutes / 60);
            return `${h}h`;
        }

        return `${totalMinutes} min`;
    };

    // V612: INITIAL STATE CALCULATION (Fixes 0% Jump Visualization)
    // We calculate the initial state based on current time to avoid animation from 0.
    const getInitialState = () => {
        const now = Date.now();
        const totalDuration = targetTime - startTime;
        const elapsed = now - startTime;
        let p = 0;

        if (totalDuration > 0) {
            p = Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));
        } else if (now >= targetTime) {
            p = 100;
        }

        const remaining = Math.max(0, targetTime - now);

        let sIdx = Math.floor((p / 100) * steps.length);
        if (isNaN(sIdx) || sIdx < 0) sIdx = 0;
        sIdx = Math.min(steps.length - 1, sIdx);

        return { p, vTime: formatVisualTime(remaining), sIdx };
    };

    const initialState = getInitialState();

    // REAL-TIME PROGRESS SYNC
    const [progress, setProgress] = useState(initialState.p);
    const [visualTime, setVisualTime] = useState(initialState.vTime);
    const [stepIndex, setStepIndex] = useState(initialState.sIdx);

    // V552: Local Ref for Auto-Skip (Replaces Window Global)
    const lastTriggerRef = useRef(0);

    // Reset loop protection when target time shifts (new job)
    useEffect(() => {
        lastTriggerRef.current = 0;
    }, [targetTime]);

    useEffect(() => {
        const updateProgress = () => {
            const now = Date.now();
            const totalDuration = targetTime - startTime;
            const elapsed = now - startTime;

            // V406: NaN Guard & Robust progress calculation
            let p = 0;
            if (totalDuration > 0) {
                p = Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));
            } else if (now >= targetTime) {
                p = 100;
            }

            // AUTO-ADVANCE TRIGGER (V523: User Request - Fix stuck loading)
            // Consolidated logic moved to end of effect to avoid duplication.

            // If very close to end but not "finished" by parent, show 99%
            // V405: Adjusted to allow 100% more easily if time is up.
            if (p >= 99.5 && isPerformingLoad && Date.now() < targetTime) p = 99.5;
            if (p >= 100 && !isPerformingLoad) p = 100;

            setProgress(p);

            // Update Time Text
            const remaining = Math.max(0, targetTime - now);
            setVisualTime(formatVisualTime(remaining));

            // AUTO-SKIP / COMPLETE if time is up OR backend finished (V524)
            // V913: ROBUST RETRY (Fixes "Stuck at 100%")
            // If timer finishes or if backend is completed entirely, we trigger completion ALWAYS. If we are stuck, retry.
            // If the cloud finished earlier, we trigger it immediately to sync with Marc's message!
            const isOverdue = remaining <= -5000;
            if (!isPerformingLoad || remaining <= 0 || isOverdue) {
                const nowTime = Date.now();
                // Retry every 4 seconds if stuck
                if (nowTime - lastTriggerRef.current > 4000) {
                    console.log(`[Loading] Timer finished or Backend Completed (!isPerformingLoad). Auto-triggering completion!`);
                    lastTriggerRef.current = nowTime;
                    // Jump to 100% instantly
                    setProgress(100);
                    if (onDevComplete) onDevComplete();
                }
            }

            // Sync "Flavor Step" to Progress roughly
            let currentStepIndex = Math.floor((p / 100) * steps.length);
            if (isNaN(currentStepIndex) || currentStepIndex < 0) currentStepIndex = 0;
            currentStepIndex = Math.min(steps.length - 1, currentStepIndex);

            setStepIndex(currentStepIndex);

            // DEBUG: Trace completion logic
            // if (p >= 99 || remaining <= 5000) {
            //    console.log(`[NutritionLoadingScreen] Progress: ${p.toFixed(2)}%, Remaining: ${remaining}ms, IsOverdue: ${isOverdue}`);
            // }
        };

        const interval = setInterval(updateProgress, 1000);
        updateProgress(); // Immediate init

        return () => clearInterval(interval);
    }, [startTime, targetTime, isPerformingLoad, steps.length, onDevComplete]); // Added onDevComplete dep if used





    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-background-dark text-white relative overflow-hidden font-inter selection:bg-primary/30">

            {/* AMBIENT BACKGROUND */}
            <div className="absolute inset-0 z-0">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[800px] bg-gradient-to-b from-primary/5 via-transparent to-transparent pointer-events-none"></div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[120px] animate-pulse-slow"></div>
            </div>

            {/* MAIN CONTENT CONTAINER */}
            <div className="relative z-10 w-full max-w-md mx-6 flex flex-col items-center">

                {/* 1. MARC AVATAR (Central & Dominant) */}
                <div className="relative mb-12 group">
                    {/* Glowing Rings */}
                    <div className="absolute -inset-4 rounded-full border border-primary/20 animate-[spin_10s_linear_infinite]"></div>
                    <div className="absolute -inset-8 rounded-full border border-white/5 animate-[spin_15s_linear_infinite_reverse]"></div>

                    {/* The Avatar */}
                    <div className="size-32 rounded-full overflow-hidden border-2 border-primary/50 shadow-[0_0_50px_rgba(242,127,13,0.3)] relative z-10">
                        <img
                            src={marcAvatarUrl || "/assets/images/landing/marc.jpg"}
                            alt="Marc Serrano"
                            className="w-full h-full object-cover object-[center_20%]"
                        />
                    </div>

                    {/* Status Badge */}
                    <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-black/80 backdrop-blur-md border border-primary/30 px-4 py-1.5 rounded-full flex items-center gap-2 shadow-xl whitespace-nowrap z-20">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                        </span>
                        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white">{t('nutrition_loading.status_working')}</span>
                    </div>
                </div>

                {/* 2. PROGRESS STEPS */}
                <div className="w-full bg-surface-card/40 backdrop-blur-2xl border border-white/10 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
                    {/* Progress Bar Top */}
                    <div className="absolute top-0 left-0 h-1 bg-primary/20 w-full">
                        <div
                            className="h-full bg-primary transition-all duration-[1000ms] ease-linear shadow-[0_0_15px_rgba(242,127,13,0.8)]"
                            style={{ width: `${progress}%` }}
                        ></div>
                    </div>

                    <div className="flex flex-col items-center text-center space-y-4 pt-4">
                        <h2 className="text-2xl font-black uppercase tracking-tight text-white animate-in fade-in slide-in-from-bottom-2 duration-500 key-{stepIndex}">
                            {!isPerformingLoad && progress >= 90 ? t('nutrition_loading.status_reviewing') : steps[stepIndex].title}
                        </h2>

                        <p className="text-xs font-bold text-primary uppercase tracking-[0.15em] animate-pulse">
                            {!isPerformingLoad && progress >= 90 ? t('nutrition_loading.status_reviewing_desc') : steps[stepIndex].desc}
                        </p>

                        {/* VISUAL TIME DISPLAY */}
                        <div className="mt-4 flex items-center gap-2 bg-black/40 px-4 py-2 rounded-full border border-white/5">
                            <span className="material-symbols-outlined text-white/50 text-sm">schedule</span>
                            <span className="text-xs font-black text-white uppercase tracking-widest">
                                {t('nutrition_loading.estimated_time')} <span className="text-primary">{visualTime}</span>
                            </span>
                        </div>



                    </div>

                    {/* V523: DEV SKIP / CONTINUE - MOVED TO TOP-RIGHT TO AVOID BOTTOM NAV (Z-100) */}
                    {userRole === 'admin' && localStorage.getItem('aura_dev_mode') !== 'false' && (
                    <button
                        onClick={onDevComplete}
                        className="fixed top-24 right-4 bg-black/60 backdrop-blur-md border border-white/10 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-[0.2em] text-white/50 hover:text-white hover:bg-primary/20 hover:border-primary/30 transition-all z-[9999] cursor-pointer"
                    >
                        {/* Show specific text if time is up */}
                        {(Date.now() >= targetTime) ? t('nutrition_loading.dev_force') : t('nutrition_loading.dev_skip')}
                    </button>
                    )}

                </div>
            </div>
        </div>
    );
};

export default NutritionLoadingScreen;
